import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ludus/ui'
import type { Manager } from '@ludus/core'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import { fullName } from '../utils/managerName.ts'
import { saveErrorKey } from './saveError.ts'

interface RetireManagerDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  manager: Manager
  onRetire: () => Promise<unknown>
}

const CONSEQUENCES = ['distribution', 'cards', 'extensions'] as const

export function RetireManagerDialog({
  open,
  onOpenChange,
  manager,
  onRetire,
}: RetireManagerDialogProps) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<PipelineApiError | null>(null)

  const retire = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await onRetire()
      onOpenChange(false)
    } catch (cause) {
      setError(cause as PipelineApiError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.managers.retire.title', { name: fullName(manager) })}
            </DialogTitle>
            <DialogDescription>{t('pages.managers.retire.body')}</DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-5">
          <ul className="flex flex-col gap-2 text-sm text-foreground">
            {CONSEQUENCES.map((key) => (
              <li key={key} className="flex gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted"
                />
                <span>{t(`pages.managers.retire.consequences.${key}`)}</span>
              </li>
            ))}
          </ul>
          {error && (
            <p role="alert" className="mt-4 text-[11px] font-medium text-toast-error-text">
              {t(saveErrorKey(error))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('pages.managers.name.cancel')}
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={retire} disabled={busy}>
            {t('pages.managers.retire.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
